import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("venus_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Turn `/api/static/...` (backend-relative) into a full URL for <img src>.
export const imgUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http")) return path;
  return `${BACKEND_URL}${path}`;
};

/** Build a responsive srcSet by swapping `foo.webp` → `foo-480.webp` for mobile.
 * Returns null if the url isn't a product WebP we have a 480px variant for. */
export const buildSrcSet = (path) => {
  if (!path || typeof path !== "string") return null;
  if (!path.endsWith(".webp") || path.includes("-480.webp")) return null;
  const full = imgUrl(path);
  const small = imgUrl(path.replace(".webp", "-480.webp"));
  // Browser picks the right one based on the <img>'s rendered width + DPR.
  return `${small} 480w, ${full} 1050w`;
};

export const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

// Public
export const fetchProducts = (params = {}) => api.get("/products", { params }).then((r) => r.data);
export const fetchProduct = (id) => api.get(`/products/${id}`).then((r) => r.data);
export const fetchConfig = () => api.get("/config").then((r) => r.data);

// Auth
export const registerApi = (payload) => api.post("/auth/register", payload).then((r) => r.data);
export const loginApi = (payload) => api.post("/auth/login", payload).then((r) => r.data);
export const meApi = () => api.get("/auth/me").then((r) => r.data);

// Orders
export const createOrderApi = (payload) => api.post("/orders", payload).then((r) => r.data);
export const verifyPaymentApi = (payload) => api.post("/orders/verify", payload).then((r) => r.data);
export const getOrderApi = (id, token) => {
  const params = token ? { t: token } : {};
  return api.get(`/orders/${id}`, { params }).then((r) => r.data);
};
export const myOrdersApi = () => api.get("/my/orders").then((r) => r.data);
export const quoteShippingApi = (payload) => api.post("/shipping/quote", payload).then((r) => r.data);

// Admin
export const adminListProducts = () => api.get("/admin/products").then((r) => r.data);
export const adminCreateProduct = (p) => api.post("/admin/products", p).then((r) => r.data);
export const adminUpdateProduct = (id, p) => api.patch(`/admin/products/${id}`, p).then((r) => r.data);
export const adminDeleteProduct = (id) => api.delete(`/admin/products/${id}`).then((r) => r.data);
export const adminListOrders = () => api.get("/admin/orders").then((r) => r.data);
export const adminUpdateOrder = (id, body) => api.patch(`/admin/orders/${id}`, body).then((r) => r.data);
export const adminDeleteOrders = (ids) => api.post("/admin/orders/delete", { ids }).then((r) => r.data);
export const validatePromoApi = (code, items) =>
  api.post("/promo/validate", { code, items: items.map((i) => ({ product_id: i.id, name: i.name, price: i.price, qty: i.qty })) }).then((r) => r.data);
export const adminListPromos = (params) => api.get("/admin/promos", { params }).then((r) => r.data);
export const adminCreatePromo = (body) => api.post("/admin/promos", body).then((r) => r.data);
export const adminUpdatePromo = (id, body) => api.patch(`/admin/promos/${id}`, body).then((r) => r.data);
export const adminPromoOrders = (id, params) => api.get(`/admin/promos/${id}/orders`, { params }).then((r) => r.data);
export const adminMarkCommissionPaid = (id, orderId) => api.post(`/admin/promos/${id}/orders/${orderId}/mark-paid`).then((r) => r.data);
export const adminMarkAllCommissionPaid = (id, params) => api.post(`/admin/promos/${id}/mark-all-paid`, null, { params }).then((r) => r.data);
export const adminPromoCsv = (id, params) => api.get(`/admin/promos/${id}/orders.csv`, { params, responseType: "blob" }).then((r) => r.data);
export const apiErrorMessage = (err) => {
  const d = err?.response?.data?.detail;
  if (Array.isArray(d)) return d.map((x) => (x.msg || "").replace(/^Value error, /, "")).join(" ");
  return d || err?.message || "Something went wrong";
};
export const adminOrderWhatsapp = (id) => api.get(`/admin/orders/${id}/whatsapp`).then((r) => r.data);
export const adminMarkWhatsappSent = (id, kind) => api.post(`/admin/orders/${id}/whatsapp/${kind}`).then((r) => r.data);
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const adminUpload = (file) => {
  const fd = new FormData();
  fd.append("file", file);
  return api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
};
export const adminChangePassword = (payload) => api.post("/admin/password", payload).then((r) => r.data);

// Commission (public)
export const submitCommissionApi = (formData) =>
  api.post("/commission", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);

// Config / discount
export const getConfigApi = () => api.get("/config").then((r) => r.data);
export const adminGetDiscount = () => api.get("/admin/discount").then((r) => r.data);
export const adminSetDiscount = (payload) => api.post("/admin/discount", payload).then((r) => r.data);

// Instagram
export const fetchInstagramFeed = () => api.get("/instagram/feed").then((r) => r.data);

// --- Newsletter / notify-me subscriber ---
export const subscribeApi = (payload) => api.post("/subscribers", payload).then((r) => r.data);

// --- Reviews ---
export const submitReviewApi = (formData) =>
  api.post("/reviews", formData, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
export const fetchProductReviews = (slugOrId) => api.get(`/products/${slugOrId}/reviews`).then((r) => r.data);
export const fetchFeaturedReviews = (limit = 6) =>
  api.get("/reviews/featured", { params: { limit } }).then((r) => r.data);
export const adminListReviews = (status) =>
  api.get("/admin/reviews", { params: status ? { status } : {} }).then((r) => r.data);
export const adminUpdateReview = (id, body) => api.patch(`/admin/reviews/${id}`, body).then((r) => r.data);
export const adminDeleteReview = (id) => api.delete(`/admin/reviews/${id}`).then((r) => r.data);
export const adminListSubscribers = () => api.get("/admin/subscribers").then((r) => r.data);

// AI shopping assistant
export const assistantHistoryApi = (sessionId) => api.get(`/assistant/history/${sessionId}`).then((r) => r.data);
